import React, { useState, useCallback } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';
import { FiGithub, FiFileText, FiActivity, FiExternalLink } from 'react-icons/fi';
import { ScrollReveal, StaggerReveal } from './ScrollReveal';
import styles from './Projects.module.css';

function TiltCard({ children, className }) {
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const [glare, setGlare] = useState({ x: 50, y: 50, opacity: 0 });

  const springX = useSpring(rotateX, { stiffness: 220, damping: 28 });
  const springY = useSpring(rotateY, { stiffness: 220, damping: 28 });

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    rotateX.set((y - 0.5) * -16);
    rotateY.set((x - 0.5) * 16);
    setGlare({ x: x * 100, y: y * 100, opacity: 0.13 });
  }, [rotateX, rotateY]);

  const handleMouseLeave = useCallback(() => {
    rotateX.set(0);
    rotateY.set(0);
    setGlare({ x: 50, y: 50, opacity: 0 });
  }, [rotateX, rotateY]);

  return (
    <motion.div
      className={className}
      style={{
        rotateX: springX,
        rotateY: springY,
        transformPerspective: 1000,
        position: 'relative',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Glare highlight */}
      <div
        aria-hidden="true"
        className={styles.glare}
        style={{
          background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,0.14), transparent 60%)`,
          opacity: glare.opacity,
        }}
      />
      {children}
    </motion.div>
  );
}

function Projects() {
  const projects = [
    {
      id: 'OP-01',
      title: 'SafeVitals XR & Edge Bridge',
      type: 'Healthcare IoT & Cybersecurity',
      description: 'XR app and Linux edge gateway streaming real-time patient telemetry to 3D dashboards. Lead Architect: secure IoT ingestion & edge containerization.',
      tags: ['React/Next.js', 'Buildroot OS', 'Raspberry Pi/MQTT', 'WebSockets'],
      status: 'Featured',
      website: 'https://safevitals.in',
    },
    {
      id: 'OP-02',
      title: 'CTF Write-ups',
      type: 'Documentation',
      description: 'Collection of detailed write-ups from IoT and embedded systems CTF challenges.',
      tags: ['CTF', 'Security'],
      status: 'Completed',
      github: 'https://github.com/balaram753/CTF-WriteUps',
      website: 'https://balaram.me/CTF-WriteUps/',
    },
    {
      id: 'OP-03',
      title: 'Docker Security Toolkit',
      type: 'Security Assessment',
      description: 'Automated toolkit using Trivy to assess Docker images and running containers for vulnerabilities and Dockerfile anti-patterns.',
      tags: ['Docker', 'Trivy', 'Python', 'Security'],
      status: 'Active',
      github: 'https://github.com/balaram753/Docker-Container-Security-Assessment/',
    },
    {
      id: 'OP-04',
      title: 'RamGaurd',
      type: 'Network IDS',
      description: 'Lightweight Python-based Network Intrusion Detection System (IDS) using Scapy. Detects ARP spoofing, TCP port scans, and ICMP ping floods with real-time console alerts.',
      tags: ['Python', 'Scapy', 'Security', 'IDS'],
      status: 'Active',
      github: 'https://github.com/balaram753/RamGaurd',
    },
    {
      id: 'OP-05',
      title: 'IoT CTF Challenge Pack',
      type: 'Security Challenges',
      description: 'Beginner-friendly IoT security challenges simulating real-world devices and firmware. Includes MQTT traffic analysis and Linux firmware extraction tasks.',
      tags: ['IoT', 'CTF', 'Firmware', 'MQTT'],
      status: 'Completed',
      github: 'https://github.com/balaram753/IoT_CTF',
    },
    {
      id: 'OP-06',
      title: 'Nurse Call System',
      type: 'Healthcare IoT',
      description: 'A dedicated nurse call communication system for healthcare environments.',
      tags: ['IoT', 'Healthcare', 'Hardware'],
      status: 'Active',
      github: 'https://github.com/balaram753/nurse-call',
    },
  ];

  return (
    <section id="projects" className={styles.projects}>
      <div className="container">
        <ScrollReveal direction="up" distance={40} duration={0.6}>
          <div className="section-header">
            <p className="label">My Work</p>
            <h2>Projects</h2>
          </div>
        </ScrollReveal>

        <StaggerReveal
          stagger={0.1}
          direction="up"
          distance={40}
          className={styles.grid}
        >
          {projects.map((project, idx) => (
            <TiltCard key={idx} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.id}>{project.id}</span>
                <span className={`${styles.status} ${styles['status' + project.status]}`}>
                  <span className={styles.statusDot} /> {project.status}
                </span>
              </div>

              <div className={styles.type}>
                <FiFileText /> {project.type}
              </div>

              <h3 className={styles.title}>{project.title}</h3>

              <div className={styles.body}>
                <p className={styles.description}>{project.description}</p>
                <div className={styles.tags}>
                  {project.tags.map((tag, tIdx) => (
                    <span key={tIdx} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              </div>

              <div className={styles.cardFooter}>
                {project.github ? (
                  <a href={project.github} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    <FiGithub /> View Source
                  </a>
                ) : project.website ? (
                  <a href={project.website} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    <FiExternalLink /> Visit Website
                  </a>
                ) : (
                  <div />
                )}
                <FiActivity className={styles.activityIcon} />
              </div>
            </TiltCard>
          ))}
        </StaggerReveal>
      </div>
    </section>
  );
}

export default Projects;
